import React, { useState } from "react";
import { qcClient, QCJobRequest, QCJobResult } from "../../api/qc_client";
import { CODM_V1_1_TEMPLATE } from "./codm_template";
import "./QCJobSubmitter.css";

interface QCJobSubmitterProps {
  onJobSubmitted: (job: QCJobResult) => void;
}

const QCJobSubmitter: React.FC<QCJobSubmitterProps> = ({ onJobSubmitted }) => {
  const [formData, setFormData] = useState<QCJobRequest>({
    asset_url: "",
    asset_name: "",
    media_type: "video",
    agent_slug: "QC_Agent_Test",
    account_id: "5",
    workspace_id: "Activision - CODM",
    instructions: "Perform full QC analysis",
    preview_url: "",
    thumbnail_url: "",
    callback_url: "",
    idempotency_key: "",
    inline_rules: "",
    metadata: {},
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [metadataInput, setMetadataInput] = useState("{}");
  const [metadataError, setMetadataError] = useState<string>("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMetadataChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const input = e.target.value;
    setMetadataInput(input);

    try {
      const metadata = JSON.parse(input);
      setFormData((prev) => ({ ...prev, metadata }));
      setMetadataError("");
    } catch {
      setMetadataError("Metadata must be valid JSON");
    }
  };

  const loadTestUrl = (type: "video" | "image") => {
    if (type === "video") {
      setFormData((prev) => ({
        ...prev,
        asset_url: "https://commondatastorage.googleapis.com/gtv-videos-library/sample/BigBuckBunny.mp4",
        asset_name: "BigBuckBunny.mp4",
        media_type: "video",
        preview_url:
          "https://peach.blender.org/wp-content/uploads/12-03-19.png?x11217",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        asset_url: "https://images.unsplash.com/photo-1579546930660-9f6ad1cda038?w=800",
        asset_name: "sample_image.jpg",
        media_type: "image",
        preview_url: "",
      }));
    }
  };

  const generateIdempotencyKey = () => {
    const key = `demo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setFormData((prev) => ({ ...prev, idempotency_key: key }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.asset_url) throw new Error("Asset URL is required");
      if (!formData.asset_name) throw new Error("Asset name is required");
      if (metadataError) throw new Error(metadataError);

      // Clean up empty strings
      const metadata = formData.metadata ?? {};
      const inlineRules = (formData.inline_rules ?? "").trim();
      const payload: QCJobRequest = {
        ...formData,
        preview_url: formData.preview_url || undefined,
        thumbnail_url: formData.thumbnail_url || undefined,
        callback_url: formData.callback_url || undefined,
        idempotency_key: formData.idempotency_key || undefined,
        inline_rules: inlineRules || undefined,
        metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
      };

      const result = await qcClient.submitJob(payload);
      setSuccessMessage(`✅ Job submitted! ID: ${result.job_id}`);
      onJobSubmitted(result);

      // Reset form
      setFormData((prev) => ({
        ...prev,
        asset_url: "",
        asset_name: "",
      }));
    } catch (err) {
      setError(`❌ Error: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="job-submitter">
      <h2>Submit QC Job</h2>

      <form onSubmit={handleSubmit} className="job-form">
        {/* Basic Fields */}
        <fieldset className="form-group">
          <legend>Basic Information</legend>

          <div className="form-row">
            <div className="form-field">
              <label htmlFor="asset_url">Asset URL *</label>
              <input
                id="asset_url"
                name="asset_url"
                type="url"
                value={formData.asset_url}
                onChange={handleChange}
                placeholder="https://example.com/video.mp4"
                className="input-field"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label htmlFor="asset_name">Asset Name *</label>
              <input
                id="asset_name"
                name="asset_name"
                type="text"
                value={formData.asset_name}
                onChange={handleChange}
                placeholder="e.g., trailer_v1.mp4"
                className="input-field"
              />
            </div>
            <div className="form-field">
              <label htmlFor="media_type">Media Type *</label>
              <select
                id="media_type"
                name="media_type"
                value={formData.media_type}
                onChange={handleChange}
                className="input-field"
              >
                <option value="video">Video</option>
                <option value="image">Image</option>
              </select>
            </div>
          </div>

          <div className="form-buttons">
            <button
              type="button"
              onClick={() => loadTestUrl("video")}
              className="btn-secondary"
            >
              📹 Load Test Video
            </button>
            <button
              type="button"
              onClick={() => loadTestUrl("image")}
              className="btn-secondary"
            >
              🖼️ Load Test Image
            </button>
          </div>
        </fieldset>

        {/* Configuration */}
        <fieldset className="form-group">
          <legend>Configuration</legend>

          <div className="form-row">
            <div className="form-field">
              <label htmlFor="agent_slug">Agent Slug</label>
              <input
                id="agent_slug"
                name="agent_slug"
                type="text"
                value={formData.agent_slug}
                onChange={handleChange}
                placeholder="e.g., call_of_duty"
                className="input-field"
              />
            </div>
            <div className="form-field">
              <label htmlFor="account_id">Account ID</label>
              <input
                id="account_id"
                name="account_id"
                type="text"
                value={formData.account_id}
                onChange={handleChange}
                placeholder="e.g., 42"
                className="input-field"
              />
            </div>
            <div className="form-field">
              <label htmlFor="workspace_id">Workspace ID</label>
              <input
                id="workspace_id"
                name="workspace_id"
                type="text"
                value={formData.workspace_id}
                onChange={handleChange}
                placeholder="e.g., 1"
                className="input-field"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field full-width">
              <label htmlFor="instructions">Instructions</label>
              <textarea
                id="instructions"
                name="instructions"
                value={formData.instructions}
                onChange={handleChange}
                placeholder="QC instructions for the agent"
                className="input-field"
                rows={2}
              />
            </div>
          </div>
        </fieldset>

        {/* Inline QC Rules */}
        <fieldset className="form-group">
          <legend>QC Rules (inline)</legend>
          <p className="form-hint">
            Paste a rules markdown file (YAML frontmatter + rules:) below to
            override Workbench config for this job. Leave blank to use the
            project default. Max 64KB.
          </p>
          <div className="form-buttons">
            <button
              type="button"
              onClick={() => setFormData((prev) => ({ ...prev, inline_rules: CODM_V1_1_TEMPLATE }))}
              className="btn-secondary"
            >
              📋 Insert CODM v1.1 Template
            </button>
            <button
              type="button"
              onClick={() => setFormData((prev) => ({ ...prev, inline_rules: "" }))}
              className="btn-secondary"
              disabled={!formData.inline_rules}
            >
              🧹 Clear Rules
            </button>
          </div>
          <div className="form-row">
            <div className="form-field full-width">
              <label htmlFor="inline_rules">
                Rules markdown{" "}
                <span className="form-hint-inline">
                  ({(formData.inline_rules ?? "").length.toLocaleString()} chars)
                </span>
              </label>
              <textarea
                id="inline_rules"
                name="inline_rules"
                value={formData.inline_rules ?? ""}
                onChange={handleChange}
                placeholder={"---\nschema_version: \"1\"\ndefault_match_threshold: 0.72\nrules:\n  - id: my_rule\n    label: My rule (§1)\n    severity: critical\n    verdict_action: fail\n    aliases:\n      - example mention\n    visual_cues:\n      - \"describe what to look for\"\n---"}
                className="input-field mono"
                rows={14}
                spellCheck={false}
              />
            </div>
          </div>
        </fieldset>

        {/* Advanced Fields */}
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="btn-expand"
        >
          {showAdvanced ? "▼" : "▶"} Advanced Options
        </button>

        {showAdvanced && (
          <fieldset className="form-group">
            <legend>Advanced</legend>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="preview_url">Preview URL</label>
                <input
                  id="preview_url"
                  name="preview_url"
                  type="url"
                  value={formData.preview_url}
                  onChange={handleChange}
                  placeholder="https://example.com/preview.jpg"
                  className="input-field"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="thumbnail_url">Thumbnail URL</label>
                <input
                  id="thumbnail_url"
                  name="thumbnail_url"
                  type="url"
                  value={formData.thumbnail_url}
                  onChange={handleChange}
                  placeholder="https://example.com/thumb.jpg"
                  className="input-field"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="callback_url">Callback URL</label>
                <input
                  id="callback_url"
                  name="callback_url"
                  type="url"
                  value={formData.callback_url}
                  onChange={handleChange}
                  placeholder="https://example.com/webhooks/qc"
                  className="input-field"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="idempotency_key">Idempotency Key</label>
                <input
                  id="idempotency_key"
                  name="idempotency_key"
                  type="text"
                  value={formData.idempotency_key}
                  onChange={handleChange}
                  placeholder="Leave empty to generate new key"
                  className="input-field"
                />
              </div>
              <div className="form-field">
                <label>&nbsp;</label>
                <button
                  type="button"
                  onClick={generateIdempotencyKey}
                  className="btn-secondary"
                >
                  Generate Key
                </button>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field full-width">
                <label htmlFor="metadata">Metadata (JSON)</label>
                <textarea
                  id="metadata"
                  value={metadataInput}
                  onChange={handleMetadataChange}
                  placeholder='{"key": "value"}'
                  className="input-field"
                  rows={3}
                />
                {metadataError && <div className="alert alert-error">{metadataError}</div>}
              </div>
            </div>
          </fieldset>
        )}

        {/* Messages */}
        {error && <div className="alert alert-error">{error}</div>}
        {successMessage && <div className="alert alert-success">{successMessage}</div>}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="btn-primary"
        >
          {loading ? "Submitting..." : "🚀 Submit Job"}
        </button>
      </form>
    </div>
  );
};

export default QCJobSubmitter;
